import { useState } from "react";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { extractApiError } from "@/lib/api";

export default function AdminChangePasswordDialog() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCurrent(""); setNext(""); setConfirm("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    if (next.length < 10) {
      toast.error("New password must be at least 10 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated.");
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(extractApiError(err, "Could not change password."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="admin-change-password-btn"
          className="border-stone-300 text-stone-800 hover:bg-stone-100 font-sans"
        >
          <KeyRound className="w-4 h-4 mr-2" />
          Change password
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="admin-change-password-dialog" className="bg-white">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-stone-900">Change admin password</DialogTitle>
          <DialogDescription className="font-sans text-stone-600">
            Use a strong password — at least 10 characters.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cp-current" className="font-sans text-xs uppercase tracking-[0.15em]">Current password</Label>
            <Input id="cp-current" data-testid="cp-current-input" type="password" value={current}
                   onChange={(e) => setCurrent(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-new" className="font-sans text-xs uppercase tracking-[0.15em]">New password</Label>
            <Input id="cp-new" data-testid="cp-new-input" type="password" value={next}
                   onChange={(e) => setNext(e.target.value)} required minLength={10} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-confirm" className="font-sans text-xs uppercase tracking-[0.15em]">Confirm new password</Label>
            <Input id="cp-confirm" data-testid="cp-confirm-input" type="password" value={confirm}
                   onChange={(e) => setConfirm(e.target.value)} required minLength={10} />
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="cp-submit-btn"
              className="bg-emerald-900 hover:bg-emerald-800 text-stone-50"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
